import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MagnifyingGlass, ArrowsClockwise, CheckCircle, XCircle, WarningCircle,
  X, PencilSimple, Receipt, CurrencyInr, Coins, Bank, Stack, DownloadSimple,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";

// ── formatting helpers ──────────────────────────────────────────────────────
const num = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, "").replace(/₹/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};
const inr = (v) => "₹" + num(v).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const inr2 = (v) => "₹" + num(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Display dates as DD-MMM-YY (e.g. 03-Sep-26). Accepts ISO "YYYY-MM-DD…"; leaves other strings as-is.
const _MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtD = (v) => {
  if (!v) return "—";
  const m = String(v).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(v);
  return `${m[3]}-${_MON[+m[2] - 1]}-${m[1].slice(2)}`;
};
const compactInr = (v) => {
  const n = num(v);
  if (n >= 1e7) return "₹" + (n / 1e7).toFixed(2) + " Cr";
  if (n >= 1e5) return "₹" + (n / 1e5).toFixed(2) + " L";
  return inr(n);
};
const dash = (v) => (v === 0 || v === "0" || !v ? "—" : v);

const card = { background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10, boxShadow: "0 1px 2px rgba(2,8,23,0.04)" };
const inputStyle = { padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, background: "#fff" };
const th = { textAlign: "left", padding: "9px 10px", fontSize: 10.5, fontWeight: 800, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "1px solid #E2E8F0", whiteSpace: "nowrap", background: "#F8FAFC", position: "sticky", top: 0, zIndex: 1 };
const td = { padding: "9px 10px", fontSize: 12.5, color: "#0F172A", borderBottom: "1px solid #F1F5F9", whiteSpace: "nowrap" };
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

function StatusPill({ status }) {
  const map = {
    mapped: { bg: "#DCFCE7", fg: "#166534", Icon: CheckCircle, label: "Mapped" },
    partial: { bg: "#FEF3C7", fg: "#92400E", Icon: WarningCircle, label: "Partial" },
    unmapped: { bg: "#FEE2E2", fg: "#991B1B", Icon: XCircle, label: "Not Mapped" },
  };
  const s = map[status] || map.unmapped;
  return (
    <span data-testid={`sba-status-${status}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 9px", borderRadius: 999, background: s.bg, color: s.fg, fontSize: 10.5, fontWeight: 800 }}>
      <s.Icon size={12} weight="fill" /> {s.label}
    </span>
  );
}

function Tile({ label, value, sub, Icon, accent, testid }) {
  return (
    <div data-testid={testid} style={{ ...card, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ display: "grid", placeItems: "center", width: 28, height: 28, borderRadius: 8, background: accent + "1A", color: accent, flex: "0 0 auto" }}>
          <Icon size={16} weight="bold" />
        </span>
        <span style={{ fontSize: 10.5, fontWeight: 800, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis" }} title={typeof value === "string" ? value : undefined}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: "#94A3B8" }}>{sub}</div>}
    </div>
  );
}

// Due = Claimed − Realized. Shows the outstanding amount prominently with a realized/claimed
// progress bar so accounting can see at a glance how much of each incentive is still to come.
function DueTile({ label, claimed, realized, Icon, accent, testid, available, onClick }) {
  const c = num(claimed), rz = num(realized);
  const due = Math.max(0, c - rz);
  const pct = c > 0 ? Math.min(100, Math.round((rz / c) * 100)) : 0;
  const settled = c > 0 && due < 0.5;
  return (
    <div data-testid={testid} onClick={onClick} role="button" tabIndex={0}
      onKeyDown={(e) => { if (onClick && (e.key === "Enter" || e.key === " ")) onClick(); }}
      onMouseEnter={(e) => { if (onClick) { e.currentTarget.style.boxShadow = "0 6px 18px rgba(2,8,23,0.12)"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
      onMouseLeave={(e) => { if (onClick) { e.currentTarget.style.boxShadow = "0 1px 2px rgba(2,8,23,0.04)"; e.currentTarget.style.transform = "none"; } }}
      style={{ ...card, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8, minWidth: 0, borderLeft: `3px solid ${accent}`, cursor: onClick ? "pointer" : "default", transition: "box-shadow .18s ease, transform .18s ease" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ display: "grid", placeItems: "center", width: 28, height: 28, borderRadius: 8, background: accent + "1A", color: accent, flex: "0 0 auto" }}>
          <Icon size={16} weight="bold" />
        </span>
        <span style={{ fontSize: 10.5, fontWeight: 800, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</span>
        {settled && <span style={{ marginLeft: "auto", fontSize: 9.5, fontWeight: 800, color: "#166534", background: "#DCFCE7", padding: "2px 7px", borderRadius: 999 }}>SETTLED</span>}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: settled ? "#16A34A" : accent, lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis" }} title={inr2(due)} data-testid={`${testid}-value`}>{compactInr(due)}</div>
      {available ? (
        <>
          <div style={{ height: 5, borderRadius: 999, background: "#E2E8F0", overflow: "hidden" }}>
            <div style={{ width: `${pct}%`, height: "100%", background: accent, borderRadius: 999, transition: "width .3s ease" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div style={{ fontSize: 11, color: "#94A3B8" }}>
              Realized <strong style={{ color: "#475569" }}>{compactInr(rz)}</strong> of {compactInr(c)} <span style={{ color: accent, fontWeight: 700 }}>· {pct}%</span>
            </div>
            {onClick && <span style={{ fontSize: 10.5, fontWeight: 700, color: accent }}>View invoices →</span>}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 11, color: "#B45309" }}>Realized data unavailable (ERP offline) — showing full claim.</div>
      )}
    </div>
  );
}

// ── Due drill-down modal: invoices still awaiting a given incentive ─────────────
const DUE_META = {
  igst: { label: "IGST", accent: "#7C3AED", Icon: Bank },
  dbk: { label: "DBK (Drawback)", accent: "#EA580C", Icon: Coins },
  rodtep: { label: "RODTEP", accent: "#0891B2", Icon: Coins },
};

function DuesDrillModal({ type, filters, onClose }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ claimed: 0, realized: 0, due: 0, count: 0, awaiting: 0 });
  const [available, setAvailable] = useState(true);
  const [onlyDue, setOnlyDue] = useState(true);
  const meta = DUE_META[type] || DUE_META.igst;

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ type });
        if (filters.q?.trim()) params.set("q", filters.q.trim());
        if (filters.fromDate) params.set("from_date", filters.fromDate);
        if (filters.toDate) params.set("to_date", filters.toDate);
        if (filters.status) params.set("status", filters.status);
        if (filters.currency) params.set("currency", filters.currency);
        if (filters.exporter) params.set("exporter", filters.exporter);
        const r = await api.get(`/shipping-bills/analytics/dues-drill?${params.toString()}`);
        if (!alive) return;
        setRows(r.data?.rows || []);
        setTotals(r.data?.totals || { claimed: 0, realized: 0, due: 0, count: 0, awaiting: 0 });
        setAvailable(r.data?.realized_available !== false);
      } catch (e) {
        if (alive) toast.error(e.response?.data?.detail || "Failed to load drill-down");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [type, filters]);

  const shown = onlyDue ? rows.filter((r) => !r.settled) : rows;
  const mth = { ...th, background: "#F1F5F9", position: "sticky", top: 0 };

  return (
    <div data-testid="sba-dues-drill-modal" onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1000, display: "grid", placeItems: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: "min(1000px, 97vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", padding: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #E2E8F0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ display: "grid", placeItems: "center", width: 34, height: 34, borderRadius: 9, background: meta.accent + "1A", color: meta.accent }}><meta.Icon size={19} weight="bold" /></span>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#0F172A" }}>{meta.label} Realization Dues</div>
              <div style={{ fontSize: 12, color: "#64748B" }}>{totals.awaiting} invoice{totals.awaiting === 1 ? "" : "s"} still awaiting · {totals.count} total</div>
            </div>
          </div>
          <button data-testid="sba-dues-drill-close" onClick={onClose} style={{ display: "grid", placeItems: "center", width: 32, height: 32, borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", cursor: "pointer" }}><X size={16} weight="bold" /></button>
        </div>

        {/* summary strip */}
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", padding: "12px 20px", borderBottom: "1px solid #F1F5F9", background: "#F8FAFC" }}>
          <div><div style={{ fontSize: 10.5, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase" }}>Claimed</div><div style={{ fontSize: 16, fontWeight: 800, color: "#0F172A" }}>{inr(totals.claimed)}</div></div>
          <div><div style={{ fontSize: 10.5, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase" }}>Realized</div><div style={{ fontSize: 16, fontWeight: 800, color: "#16A34A" }}>{inr(totals.realized)}</div></div>
          <div><div style={{ fontSize: 10.5, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase" }}>Due</div><div style={{ fontSize: 16, fontWeight: 800, color: meta.accent }}>{inr(totals.due)}</div></div>
          <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#475569", fontWeight: 600, cursor: "pointer" }}>
            <input data-testid="sba-dues-drill-onlydue" type="checkbox" checked={onlyDue} onChange={(e) => setOnlyDue(e.target.checked)} /> Show only outstanding
          </label>
        </div>
        {!available && (
          <div style={{ padding: "8px 20px", fontSize: 12, color: "#B45309", background: "#FFFBEB" }}>Realized data unavailable (ERP offline) — dues equal the full claim.</div>
        )}

        <div style={{ overflow: "auto", flex: 1 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
            <thead>
              <tr>
                <th style={mth}>Export Inv#</th>
                <th style={mth}>GST Inv#</th>
                <th style={mth}>Customer</th>
                <th style={mth}>SB No(s)</th>
                <th style={{ ...mth, textAlign: "right" }}>Claimed</th>
                <th style={{ ...mth, textAlign: "right" }}>Realized</th>
                <th style={{ ...mth, textAlign: "right" }}>Due</th>
                <th style={{ ...mth, textAlign: "center" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={r.sale_no || i} data-testid={`sba-dues-drill-row-${i}`}>
                  <td style={{ ...td, fontWeight: 700 }}>{r.export_invoice_number || "—"}</td>
                  <td style={td}>{r.gst_invoice_number || "—"}</td>
                  <td style={{ ...td, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }} title={r.customer_name}>{r.customer_name || "—"}</td>
                  <td style={td}>{(r.shipping_bill_nos || []).join(", ") || "—"}</td>
                  <td style={tdNum}>{inr(r.claimed)}</td>
                  <td style={{ ...tdNum, color: "#16A34A" }}>{inr(r.realized)}</td>
                  <td style={{ ...tdNum, fontWeight: 800, color: r.settled ? "#16A34A" : meta.accent }}>{inr(r.due)}</td>
                  <td style={{ ...td, textAlign: "center" }}>
                    {r.settled
                      ? <span style={{ fontSize: 10, fontWeight: 800, color: "#166534", background: "#DCFCE7", padding: "2px 8px", borderRadius: 999 }}>SETTLED</span>
                      : <span style={{ fontSize: 10, fontWeight: 800, color: "#92400E", background: "#FEF3C7", padding: "2px 8px", borderRadius: 999 }}>AWAITING</span>}
                  </td>
                </tr>
              ))}
              {!loading && shown.length === 0 && (
                <tr><td colSpan={8} style={{ ...td, textAlign: "center", padding: 40, color: "#94A3B8" }}>{onlyDue ? "Nothing outstanding — all claims of this type are realized." : "No invoices with this claim."}</td></tr>
              )}
              {loading && (
                <tr><td colSpan={8} style={{ ...td, textAlign: "center", padding: 40, color: "#94A3B8" }}>Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


function DetailRow({ label, value }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, padding: "6px 0", borderBottom: "1px solid #F1F5F9" }}>
      <span style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 12.5, color: "#0F172A", fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}>{value || "—"}</span>
    </div>
  );
}

function DetailModal({ row, onClose, onEdit }) {
  if (!row) return null;
  const f = row;
  return (
    <div data-testid="sba-detail-modal" onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1000, display: "grid", placeItems: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: "min(860px, 96vw)", maxHeight: "90vh", overflow: "auto", padding: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #E2E8F0", position: "sticky", top: 0, background: "#fff", zIndex: 2 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#0F172A" }}>Shipping Bill #{f.shipping_bill_no || "—"}</div>
            <div style={{ fontSize: 12, color: "#64748B" }}>{f.exporter_name || "—"} · {fmtD(f.shipping_bill_date)}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <StatusPill status={f.mapped_status || "unmapped"} />
            <button data-testid="sba-detail-edit" onClick={() => onEdit(f)} className="jde-btn"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, padding: "7px 12px", border: "1px solid #CBD5E1", borderRadius: 8, background: "#fff", cursor: "pointer", fontWeight: 700 }}>
              <PencilSimple size={14} weight="bold" /> Edit in Upload
            </button>
            <button data-testid="sba-detail-close" onClick={onClose} style={{ display: "grid", placeItems: "center", width: 32, height: 32, borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", cursor: "pointer" }}>
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>

        <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", marginBottom: 4 }}>Document</div>
            <DetailRow label="SB No." value={f.shipping_bill_no} />
            <DetailRow label="SB Date" value={fmtD(f.shipping_bill_date)} />
            <DetailRow label="Port Code" value={f.port_code} />
            <DetailRow label="IEC Number" value={f.iec_number} />
            <DetailRow label="Exporter" value={f.exporter_name} />
            <DetailRow label="Customer" value={(f.mapping?.containers || []).find((c) => c.party)?.party} />

            <div style={{ fontSize: 11, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", margin: "16px 0 4px" }}>Invoices</div>
            <DetailRow label="Export Invoice (ERP)" value={f.export_invoice_number} />
            <DetailRow label="Export Invoice (on SB)" value={f.export_invoice_sb} />
            <DetailRow label="Export Invoice Date" value={fmtD(f.export_invoice_date)} />
            <DetailRow label="GST Invoice No." value={f.gst_invoice_number} />
            <DetailRow label="GST Invoice Date" value={fmtD(f.gst_invoice_date)} />
            <DetailRow label="Contract No. & Dt" value={f.contract_no} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", marginBottom: 4 }}>Financials</div>
            <DetailRow label="Currency" value={f.currency} />
            <DetailRow label="Exchange Rate" value={f.exchange_rate} />
            <DetailRow label="Invoice Value (FC)" value={f.invoice_value_fc ? Number(f.invoice_value_fc).toLocaleString("en-US", { maximumFractionDigits: 2 }) : ""} />
            <DetailRow label="FOB Value" value={f.fob_value ? inr2(f.fob_value) : ""} />
            <DetailRow label="Freight" value={f.freight ? inr2(f.freight) : ""} />
            <DetailRow label="Insurance" value={f.insurance ? inr2(f.insurance) : ""} />
            <DetailRow label="DBK Claim" value={f.dbk_claim ? inr2(f.dbk_claim) : ""} />
            <DetailRow label="Taxable Value" value={f.igst_value ? inr2(f.igst_value) : ""} />
            <DetailRow label="IGST Amount" value={f.igst_amount ? inr2(f.igst_amount) : ""} />
            <DetailRow label="RODTEP Amount" value={f.rodtep_amount ? inr2(f.rodtep_amount) : ""} />

            <div style={{ fontSize: 11, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", margin: "16px 0 4px" }}>Containers</div>
            {(f.containers && f.containers.length > 0) ? f.containers.map((c, i) => (
              <DetailRow key={i} label={c.container_no || "—"} value={c.seal_no ? `Seal ${c.seal_no}` : ""} />
            )) : <div style={{ fontSize: 12, color: "#94A3B8" }}>No containers</div>}
          </div>
        </div>
        <div style={{ padding: "0 20px 18px", fontSize: 11.5, color: "#94A3B8" }}>
          Read-only view. To change any value, click <strong>Edit in Upload</strong> — edits are made on the Upload Shipping Bill page.
        </div>
      </div>
    </div>
  );
}

const EMPTY_TOTALS = { count: 0, fob_value: 0, igst_value: 0, igst_amount: 0, dbk_claim: 0, rodtep_amount: 0, freight: 0, insurance: 0, mapped: 0, partial: 0, unmapped: 0, igst_realized: 0, dbk_realized: 0, rodtep_realized: 0, igst_due: 0, dbk_due: 0, rodtep_due: 0, realized_available: true };

export default function ShippingBillAnalytics() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");
  const [currency, setCurrency] = useState("");
  const [exporter, setExporter] = useState("");
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(EMPTY_TOTALS);
  const [currencies, setCurrencies] = useState([]);
  const [exporters, setExporters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [drillType, setDrillType] = useState(null); // 'igst' | 'dbk' | 'rodtep' | null
  const drillFilters = useMemo(() => ({ q, fromDate, toDate, status, currency, exporter }), [q, fromDate, toDate, status, currency, exporter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      if (status) params.set("status", status);
      if (currency) params.set("currency", currency);
      if (exporter) params.set("exporter", exporter);
      const r = await api.get(`/shipping-bills/analytics?${params.toString()}`);
      setRows(r.data?.rows || []);
      setTotals(r.data?.totals || EMPTY_TOTALS);
      setCurrencies(r.data?.currencies || []);
      setExporters(r.data?.exporters || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load analytics");
    } finally { setLoading(false); }
  }, [q, fromDate, toDate, status, currency, exporter]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Debounced re-load on filter change.
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [q, fromDate, toDate, status, currency, exporter, load]);

  const clearFilters = () => { setQ(""); setFromDate(""); setToDate(""); setStatus(""); setCurrency(""); };
  const hasFilters = q || fromDate || toDate || status || currency;

  const [downloading, setDownloading] = useState(false);
  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      if (status) params.set("status", status);
      if (currency) params.set("currency", currency);
      if (exporter) params.set("exporter", exporter);
      const r = await api.get(`/shipping-bills/analytics.xlsx?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `shipping_bill_analytics_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to export Excel");
    } finally { setDownloading(false); }
  };

  const [backfilling, setBackfilling] = useState(false);
  const backfillRates = async () => {
    if (!window.confirm("Re-process ALL saved shipping bills from their PDFs?\nThis refreshes exchange rate, item lines and mapping under the latest logic. It runs in the background and may take a while.")) return;
    setBackfilling(true);
    try {
      await api.post("/shipping-bills/reprocess-all");
      toast.info("Backfill started — re-processing in the background…");
      // Poll status until done.
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get("/shipping-bills/reprocess-all/status");
          if (!data.running) {
            clearInterval(poll);
            setBackfilling(false);
            toast.success(`Backfill done — ${data.processed} updated${data.failed ? `, ${data.failed} failed` : ""}${data.skipped ? `, ${data.skipped} skipped` : ""}`);
            load();
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      setBackfilling(false);
      toast.error(e.response?.data?.detail || "Failed to start backfill");
    }
  };

  const openDetail = async (row) => {
    // fetch the full record so items/all fields are available
    try {
      const r = await api.get(`/shipping-bills/${row.id}`);
      setDetail(r.data || row);
    } catch {
      setDetail(row);
    }
  };

  const editInUpload = (row) => {
    // The Upload page re-opens a saved record for editing.
    navigate(`/logistics/shipping-bills?open=${row.id}`);
  };

  return (
    <Layout>
      <div data-testid="sba-page" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
              <Receipt size={24} weight="bold" style={{ color: "#0EA5E9" }} /> Shipping Bill Analytics
            </h1>
            <p style={{ fontSize: 13, color: "#64748B", margin: "4px 0 0" }}>
              Financial roll-up of export shipping bills — FOB, IGST, DBK, RODTEP & more for accounting.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button data-testid="sba-backfill" onClick={backfillRates} disabled={backfilling || totals.count === 0}
              title="Re-process all saved shipping bills from their PDFs (refresh exchange rate, items & mapping)"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", border: "1px solid #CBD5E1", borderRadius: 8, background: "#fff", cursor: backfilling ? "wait" : "pointer", fontSize: 13, fontWeight: 700, color: "#0F172A" }}>
              <ArrowsClockwise size={15} weight="bold" className={backfilling ? "spin" : ""} /> {backfilling ? "Backfilling…" : "Backfill Rates"}
            </button>
            <button data-testid="sba-download-xlsx" onClick={downloadXlsx} disabled={downloading || totals.count === 0}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", border: "none", borderRadius: 8, background: totals.count === 0 ? "#94A3B8" : "#16A34A", cursor: totals.count === 0 ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, color: "#fff" }}>
              <DownloadSimple size={15} weight="bold" /> {downloading ? "Exporting…" : "Download XLSX"}
            </button>
            <button data-testid="sba-refresh" onClick={load} disabled={loading}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", border: "1px solid #CBD5E1", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#0F172A" }}>
              <ArrowsClockwise size={15} weight="bold" className={loading ? "spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        {/* Exporter toggle — scope the whole page (tiles, dues & table) to one exporter */}
        {exporters.length > 1 && (
          <div data-testid="sba-exporter-toggle" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.4 }}>Exporter</span>
            <div style={{ display: "inline-flex", flexWrap: "wrap", gap: 4, background: "#F1F5F9", padding: 4, borderRadius: 10 }}>
              {["", ...exporters].map((ex) => {
                const active = exporter === ex;
                return (
                  <button key={ex || "all"} data-testid={`sba-exporter-${ex ? ex.replace(/\s+/g, "-").toLowerCase() : "all"}`}
                    onClick={() => setExporter(ex)}
                    style={{
                      padding: "6px 14px", borderRadius: 7, border: "none", cursor: "pointer",
                      fontSize: 12.5, fontWeight: 700, whiteSpace: "nowrap",
                      background: active ? "#0EA5E9" : "transparent",
                      color: active ? "#fff" : "#475569",
                      boxShadow: active ? "0 1px 3px rgba(2,8,23,0.18)" : "none",
                      transition: "background-color .15s ease, color .15s ease",
                    }}>
                    {ex || "All exporters"}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Tiles */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <Tile testid="sba-tile-count" label="Shipping Bills" value={totals.count} sub={`${totals.mapped} mapped · ${totals.unmapped} unmapped`} Icon={Stack} accent="#0EA5E9" />
          <Tile testid="sba-tile-fob" label="Total FOB Value" value={compactInr(totals.fob_value)} sub={inr(totals.fob_value)} Icon={CurrencyInr} accent="#16A34A" />
          <Tile testid="sba-tile-igst-amt" label="IGST Amount" value={compactInr(totals.igst_amount)} sub={`Taxable value ${compactInr(totals.igst_value)}`} Icon={Bank} accent="#7C3AED" />
          <Tile testid="sba-tile-dbk" label="DBK Claim" value={compactInr(totals.dbk_claim)} sub={inr(totals.dbk_claim)} Icon={Coins} accent="#EA580C" />
          <Tile testid="sba-tile-rodtep" label="RODTEP Amount" value={compactInr(totals.rodtep_amount)} sub={inr(totals.rodtep_amount)} Icon={Coins} accent="#0891B2" />
        </div>

        {/* Realization dues — how much of each incentive is still to be received (from FA-Logistics receipts) */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.5 }}>
            <span>Realization Dues</span>
            <span style={{ flex: 1, height: 1, background: "#E2E8F0" }} />
            <span style={{ fontWeight: 600, textTransform: "none", letterSpacing: 0, fontSize: 11, color: "#94A3B8" }}>Claim − Received (from FA-Logistics)</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <DueTile testid="sba-tile-igst-due" label="IGST Due" claimed={totals.igst_amount} realized={totals.igst_realized} Icon={Bank} accent="#7C3AED" available={totals.realized_available !== false} onClick={() => setDrillType("igst")} />
            <DueTile testid="sba-tile-dbk-due" label="DBK Due" claimed={totals.dbk_claim} realized={totals.dbk_realized} Icon={Coins} accent="#EA580C" available={totals.realized_available !== false} onClick={() => setDrillType("dbk")} />
            <DueTile testid="sba-tile-rodtep-due" label="RODTEP Due" claimed={totals.rodtep_amount} realized={totals.rodtep_realized} Icon={Coins} accent="#0891B2" available={totals.realized_available !== false} onClick={() => setDrillType("rodtep")} />
          </div>
        </div>

        {/* Filters */}
        <div style={{ ...card, padding: 12, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <div style={{ position: "relative", flex: "1 1 260px", minWidth: 220 }}>
            <MagnifyingGlass size={15} weight="bold" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94A3B8" }} />
            <input data-testid="sba-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search SB no / exporter / invoice / container…"
              style={{ ...inputStyle, width: "100%", paddingLeft: 32 }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>SB Date</span>
            <input data-testid="sba-from" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inputStyle} />
            <span style={{ color: "#94A3B8" }}>–</span>
            <input data-testid="sba-to" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inputStyle} />
          </div>
          <select data-testid="sba-status" value={status} onChange={(e) => setStatus(e.target.value)} style={inputStyle}>
            <option value="">All statuses</option>
            <option value="mapped">Mapped</option>
            <option value="partial">Partial</option>
            <option value="unmapped">Not Mapped</option>
          </select>
          <select data-testid="sba-currency" value={currency} onChange={(e) => setCurrency(e.target.value)} style={inputStyle}>
            <option value="">All currencies</option>
            {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {hasFilters && (
            <button data-testid="sba-clear" onClick={clearFilters}
              style={{ padding: "8px 12px", border: "1px solid #E2E8F0", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 12.5, fontWeight: 700, color: "#64748B" }}>
              Clear
            </button>
          )}
        </div>

        {/* Table */}
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          <div style={{ overflowX: "auto", maxHeight: "60vh", overflowY: "auto" }}>
            <table data-testid="sba-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 1860 }}>
              <thead>
                <tr>
                  <th style={th}>SB No.</th>
                  <th style={th}>SB Date</th>
                  <th style={th}>Plant Code</th>
                  <th style={th}>Exporter</th>
                  <th style={th}>Customer</th>
                  <th style={th}>Port</th>
                  <th style={th}>Export Inv#</th>
                  <th style={th}>Export Inv Date</th>
                  <th style={th}>GST Inv#</th>
                  <th style={th}>GST Inv Date</th>
                  <th style={{ ...th, textAlign: "center" }}>Ccy</th>
                  <th style={{ ...th, textAlign: "right" }}>Exch. Rate</th>
                  <th style={{ ...th, textAlign: "right" }}>Inv Value (FC)</th>
                  <th style={{ ...th, textAlign: "right" }}>FOB Value</th>
                  <th style={{ ...th, textAlign: "right" }}>Taxable Value</th>
                  <th style={{ ...th, textAlign: "right" }}>IGST Amt</th>
                  <th style={{ ...th, textAlign: "right" }}>DBK</th>
                  <th style={{ ...th, textAlign: "right" }}>RODTEP</th>
                  <th style={th}>Container</th>
                  <th style={th}>Seal</th>
                  <th style={{ ...th, textAlign: "center" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`sba-row-${r.id}`} onClick={() => openDetail(r)}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#F8FAFC")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                    <td style={{ ...td, fontWeight: 700, color: "#0EA5E9" }}>{r.shipping_bill_no || "—"}</td>
                    <td style={td}>{fmtD(r.shipping_bill_date)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{r.plant_code || "—"}</td>
                    <td style={{ ...td, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }} title={r.exporter_name}>{r.exporter_name || "—"}</td>
                    <td style={{ ...td, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }} title={r.customer_name}>{r.customer_name || "—"}</td>
                    <td style={td}>{r.port_code || "—"}</td>
                    <td style={td}>{r.export_invoice_number || dash(r.export_invoices?.[0])}</td>
                    <td style={td}>{fmtD(r.export_invoice_date)}</td>
                    <td style={td}>{r.gst_invoice_number || "—"}</td>
                    <td style={td}>{fmtD(r.gst_invoice_date)}</td>
                    <td style={{ ...td, textAlign: "center" }}>{r.currency || "—"}</td>
                    <td style={tdNum}>{dash(r.exchange_rate)}</td>
                    <td style={tdNum}>{r.invoice_value_fc ? Number(r.invoice_value_fc).toLocaleString("en-US", { maximumFractionDigits: 2 }) : "—"}</td>
                    <td style={{ ...tdNum, fontWeight: 700 }}>{r.fob_value ? inr(r.fob_value) : "—"}</td>
                    <td style={tdNum}>{r.igst_value ? inr(r.igst_value) : "—"}</td>
                    <td style={tdNum}>{r.igst_amount ? inr(r.igst_amount) : "—"}</td>
                    <td style={tdNum}>{r.dbk_claim ? inr(r.dbk_claim) : "—"}</td>
                    <td style={tdNum}>{r.rodtep_amount ? inr(r.rodtep_amount) : "—"}</td>
                    <td style={td}>{(r.container_nos || []).join(", ") || "—"}</td>
                    <td style={td}>{(r.seal_nos || []).join(", ") || "—"}</td>
                    <td style={{ ...td, textAlign: "center" }}><StatusPill status={r.mapped_status || "unmapped"} /></td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={21} style={{ ...td, textAlign: "center", padding: 40, color: "#94A3B8" }}>
                      {loading ? "Loading…" : "No shipping bills match your filters."}
                    </td>
                  </tr>
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr style={{ background: "#F8FAFC", position: "sticky", bottom: 0 }}>
                    <td style={{ ...td, fontWeight: 800 }} colSpan={13}>Total ({totals.count})</td>
                    <td style={{ ...tdNum, fontWeight: 800 }}>{inr(totals.fob_value)}</td>
                    <td style={{ ...tdNum, fontWeight: 800 }}>{inr(totals.igst_value)}</td>
                    <td style={{ ...tdNum, fontWeight: 800 }}>{inr(totals.igst_amount)}</td>
                    <td style={{ ...tdNum, fontWeight: 800 }}>{inr(totals.dbk_claim)}</td>
                    <td style={{ ...tdNum, fontWeight: 800 }}>{inr(totals.rodtep_amount)}</td>
                    <td style={td} colSpan={3}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      <DetailModal row={detail} onClose={() => setDetail(null)} onEdit={editInUpload} />
      {drillType && (
        <DuesDrillModal type={drillType} filters={drillFilters} onClose={() => setDrillType(null)} />
      )}
    </Layout>
  );
}
